import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProjectCard from '../components/ProjectCard';
import Newsletter from '../components/Newsletter';

const featuredProjects = [
  {
    id: 1,
    title: "LingoBridge AI",
    description: "An AI-powered translation layer for oral-only indigenous languages, helping community elders preserve knowledge through digital accessibility.",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    tags: ["NLP", "PyTorch", "Next.js"],
    demoLink: "#",
    repoLink: "#"
  },
  {
    id: 2,
    title: "EquityFlow",
    description: "Predictive resource allocation tool built for local non-profits to identify underserved zones and optimize food/medical supply distribution.",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    tags: ["Data Science", "Python", "React"],
    demoLink: "#",
    repoLink: "#"
  }
];

const Home = () => {
  return (
    <div className="space-y-32">
      <section className="pt-12 md:pt-24 flex flex-col md:flex-row items-center gap-12">
        <motion.div 
          className="flex-1 space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-mono">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Building for Social Impact
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-tight">
            Making AI <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">accessible & relatable</span> for all.
          </h1>
          
          <p className="text-lg text-slate-400 max-w-xl leading-relaxed">
            I'm an indie developer on a mission to bridge the digital divide. I build AI-powered apps that empower underserved communities and make complex technology feel like a helpful neighbor.
          </p>
          
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/lab" className="btn-primary flex items-center gap-2">
              Explore The Lab <SafeIcon icon={FiIcons.FiArrowRight} />
            </Link>
            <Link to="/contact" className="btn-secondary flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiUsers} /> Collaborate
            </Link>
          </div>
        </motion.div>

        <motion.div 
          className="md:w-1/3 relative"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="aspect-square rounded-2xl overflow-hidden glass-card p-2 relative z-10">
            <img 
              src="https://images.unsplash.com/photo-1620712943543-bcc4628c9757?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="Human-Centric AI" 
              className="w-full h-full object-cover rounded-xl opacity-80 mix-blend-luminosity hover:mix-blend-normal transition-all duration-500"
            />
          </div>
          <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-500/20 to-purple-500/20 rounded-3xl blur-2xl -z-10"></div>
        </motion.div>
      </section>

      <section>
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Impact from The Lab</h2>
            <p className="text-slate-400">AI experiments designed for a more equitable future.</p>
          </div>
          <Link to="/lab" className="hidden md:flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium">
            View all projects <SafeIcon icon={FiIcons.FiArrowRight} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featuredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <ProjectCard project={project} />
            </motion.div>
          ))}
        </div>
      </section>

      <section>
        <Newsletter />
      </section>
    </div>
  );
};

export default Home;